import React, { useState, useRef, useEffect } from 'react';
import { 
  X, Check, Move, Maximize2, Sparkles, RefreshCw, 
  Layers, SunMedium, RotateCw, Eye, Undo2
} from 'lucide-react';
import { useTheme } from '../../../components/theme/ThemeProvider';

interface LogoPositionerModalProps {
  isOpen: boolean;
  onClose: () => void;
  backgroundUrl: string;
  logoUrl: string;
  creativeTitle?: string;
  onSave: (compositedBase64: string) => Promise<void>;
}

export function LogoPositionerModal({
  isOpen,
  onClose,
  backgroundUrl,
  logoUrl,
  creativeTitle = 'Ajuste Manual da Logo',
  onSave
}: LogoPositionerModalProps) {
  const { theme } = useTheme();
  const isLight = theme === 'light';

  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const containerRef = useRef<HTMLDivElement | null>(null);

  // Estados de Imagem
  const [bgImage, setBgImage] = useState<HTMLImageElement | null>(null);
  const [logoImage, setLogoImage] = useState<HTMLImageElement | null>(null);
  const [imagesLoaded, setImagesLoaded] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  // Estados de Posicionamento da Logo (percentuais 0-100 para ser independente de resolução)
  const [posX, setPosX] = useState(85); // 85% = canto inferior/superior direito
  const [posY, setPosY] = useState(88); // 88% = canto inferior
  const [scalePercent, setScalePercent] = useState(20); // 20% da largura do canvas
  const [opacity, setOpacity] = useState(1.0); // 100%
  const [rotation, setRotation] = useState(0); // graus
  const [hasShadow, setHasShadow] = useState(true);

  // Estado de Arrastar (Drag)
  const [isDragging, setIsDragging] = useState(false);
  const dragStartPos = useRef<{ mouseX: number; mouseY: number; startX: number; startY: number }>({
    mouseX: 0,
    mouseY: 0,
    startX: 85,
    startY: 88
  });

  // Carregar Imagens
  useEffect(() => {
    if (!isOpen || !backgroundUrl || !logoUrl) return;

    setImagesLoaded(false);
    let isMounted = true;

    const loadImg = (url: string) =>
      new Promise<HTMLImageElement>((resolve, reject) => {
        const img = new Image();
        img.crossOrigin = 'anonymous';
        img.onload = () => resolve(img);
        img.onerror = () => reject(new Error(`Falha ao carregar ${url}`));
        img.src = url;
      });

    Promise.all([loadImg(backgroundUrl), loadImg(logoUrl)])
      .then(([bg, logo]) => {
        if (!isMounted) return;
        setBgImage(bg);
        setLogoImage(logo);
        setImagesLoaded(true);

        // Se for vertical (Stories 9:16), posiciona no canto inferior com safe zone
        const isVertical = (bg.naturalHeight || bg.height) > (bg.naturalWidth || bg.width) * 1.3;
        if (isVertical) {
          setPosX(82);
          setPosY(86);
          setScalePercent(24);
        } else {
          setPosX(85);
          setPosY(88);
          setScalePercent(20);
        }
      })
      .catch((err) => {
        console.error('[LogoPositioner] Erro ao carregar imagens:', err);
        alert('Não foi possível carregar a imagem ou logo para composição.');
        onClose();
      });

    return () => {
      isMounted = false;
    };
  }, [isOpen, backgroundUrl, logoUrl]);

  // Renderizar Canvas Interativo
  useEffect(() => {
    if (!imagesLoaded || !bgImage || !logoImage || !canvasRef.current) return;

    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d', { alpha: false });
    if (!ctx) return;

    const bgW = bgImage.naturalWidth || bgImage.width || 1080;
    const bgH = bgImage.naturalHeight || bgImage.height || 1080;

    canvas.width = bgW;
    canvas.height = bgH;
    ctx.imageSmoothingEnabled = true;
    ctx.imageSmoothingQuality = 'high';

    // 1. Desenhar Fundo (Criativo)
    ctx.drawImage(bgImage, 0, 0, bgW, bgH);

    // 2. Calcular dimensões da Logo
    const logoW = logoImage.naturalWidth || logoImage.width || 100;
    const logoH = logoImage.naturalHeight || logoImage.height || 100;
    const aspect = logoW / logoH;

    const finalLogoW = (scalePercent / 100) * bgW;
    const finalLogoH = finalLogoW / aspect;

    const centerX = (posX / 100) * bgW;
    const centerY = (posY / 100) * bgH;

    // 3. Desenhar Logo com transformações
    ctx.save();
    ctx.globalAlpha = opacity;
    ctx.translate(centerX, centerY);

    if (rotation !== 0) {
      ctx.rotate((rotation * Math.PI) / 180);
    }

    if (hasShadow) {
      ctx.shadowColor = 'rgba(0, 0, 0, 0.6)';
      ctx.shadowBlur = Math.round(bgW * 0.015);
      ctx.shadowOffsetX = 0;
      ctx.shadowOffsetY = Math.round(bgH * 0.005);
    }

    ctx.drawImage(logoImage, -finalLogoW / 2, -finalLogoH / 2, finalLogoW, finalLogoH);
    ctx.restore();

    // 4. Desenhar Bounding Box de Seleção se estiver no modo interativo
    ctx.save();
    ctx.translate(centerX, centerY);
    if (rotation !== 0) ctx.rotate((rotation * Math.PI) / 180);

    ctx.strokeStyle = '#6366f1';
    ctx.lineWidth = Math.max(2, Math.round(bgW * 0.0025));
    ctx.setLineDash([6, 6]);
    ctx.strokeRect(-finalLogoW / 2 - 4, -finalLogoH / 2 - 4, finalLogoW + 8, finalLogoH + 8);

    // Alças nos 4 cantos
    const handleSize = Math.max(8, Math.round(bgW * 0.012));
    ctx.fillStyle = '#ffffff';
    ctx.strokeStyle = '#4f46e5';
    ctx.lineWidth = 2;
    ctx.setLineDash([]);

    const corners = [
      [-finalLogoW / 2 - 4, -finalLogoH / 2 - 4],
      [finalLogoW / 2 + 4, -finalLogoH / 2 - 4],
      [-finalLogoW / 2 - 4, finalLogoH / 2 + 4],
      [finalLogoW / 2 + 4, finalLogoH / 2 + 4]
    ];

    for (const [cx, cy] of corners) {
      ctx.fillRect(cx - handleSize / 2, cy - handleSize / 2, handleSize, handleSize);
      ctx.strokeRect(cx - handleSize / 2, cy - handleSize / 2, handleSize, handleSize);
    }

    ctx.restore();
  }, [imagesLoaded, bgImage, logoImage, posX, posY, scalePercent, opacity, rotation, hasShadow]);

  // Handlers de Mouse/Touch para Arrastar
  const handlePointerDown = (e: React.PointerEvent<HTMLCanvasElement>) => {
    if (!canvasRef.current) return;
    const rect = canvasRef.current.getBoundingClientRect();
    const mouseX = e.clientX - rect.left;
    const mouseY = e.clientY - rect.top;

    setIsDragging(true);
    dragStartPos.current = {
      mouseX,
      mouseY,
      startX: posX,
      startY: posY
    };

    (e.target as HTMLElement).setPointerCapture(e.pointerId);
  };

  const handlePointerMove = (e: React.PointerEvent<HTMLCanvasElement>) => {
    if (!isDragging || !canvasRef.current) return;
    const rect = canvasRef.current.getBoundingClientRect();
    const mouseX = e.clientX - rect.left;
    const mouseY = e.clientY - rect.top;

    const deltaXPercent = ((mouseX - dragStartPos.current.mouseX) / rect.width) * 100;
    const deltaYPercent = ((mouseY - dragStartPos.current.mouseY) / rect.height) * 100;

    let newX = dragStartPos.current.startX + deltaXPercent;
    let newY = dragStartPos.current.startY + deltaYPercent;

    // Limites de segurança (0% a 100%)
    newX = Math.max(5, Math.min(95, newX));
    newY = Math.max(5, Math.min(95, newY));

    setPosX(Math.round(newX * 10) / 10);
    setPosY(Math.round(newY * 10) / 10);
  };

  const handlePointerUp = (e: React.PointerEvent<HTMLCanvasElement>) => {
    if (isDragging) {
      setIsDragging(false);
      try {
        (e.target as HTMLElement).releasePointerCapture(e.pointerId);
      } catch (_) {}
    }
  };

  // Presets Rápidos
  const applyPreset = (preset: 'top-right' | 'top-left' | 'bottom-right' | 'bottom-left' | 'center') => {
    switch (preset) {
      case 'top-right':
        setPosX(85);
        setPosY(14);
        break;
      case 'top-left':
        setPosX(15);
        setPosY(14);
        break;
      case 'bottom-right':
        setPosX(85);
        setPosY(88);
        break;
      case 'bottom-left':
        setPosX(15);
        setPosY(88);
        break;
      case 'center':
        setPosX(50);
        setPosY(50);
        break;
    }
  };

  // Exportar e Salvar Imagem Final Limpa (sem bounding box)
  const handleSave = async () => {
    if (!bgImage || !logoImage) return;

    try {
      setIsSaving(true);

      // Criar canvas de exportação limpo na resolução total da arte
      const exportCanvas = document.createElement('canvas');
      const bgW = bgImage.naturalWidth || bgImage.width || 1080;
      const bgH = bgImage.naturalHeight || bgImage.height || 1080;
      exportCanvas.width = bgW;
      exportCanvas.height = bgH;
      const ctx = exportCanvas.getContext('2d', { alpha: false });
      if (!ctx) throw new Error('Não foi possível inicializar o canvas de exportação');

      ctx.imageSmoothingEnabled = true;
      ctx.imageSmoothingQuality = 'high';

      // 1. Fundo
      ctx.drawImage(bgImage, 0, 0, bgW, bgH);

      // 2. Logo
      const logoW = logoImage.naturalWidth || logoImage.width || 100;
      const logoH = logoImage.naturalHeight || logoImage.height || 100;
      const aspect = logoW / logoH;

      const finalLogoW = (scalePercent / 100) * bgW;
      const finalLogoH = finalLogoW / aspect;
      const centerX = (posX / 100) * bgW;
      const centerY = (posY / 100) * bgH;

      ctx.save();
      ctx.globalAlpha = opacity;
      ctx.translate(centerX, centerY);

      if (rotation !== 0) ctx.rotate((rotation * Math.PI) / 180);

      if (hasShadow) {
        ctx.shadowColor = 'rgba(0, 0, 0, 0.6)';
        ctx.shadowBlur = Math.round(bgW * 0.015);
        ctx.shadowOffsetX = 0;
        ctx.shadowOffsetY = Math.round(bgH * 0.005);
      }

      ctx.drawImage(logoImage, -finalLogoW / 2, -finalLogoH / 2, finalLogoW, finalLogoH);
      ctx.restore();

      const compositedBase64 = exportCanvas.toDataURL('image/jpeg', 0.95);
      await onSave(compositedBase64);
      onClose();
    } catch (err: any) {
      console.error('[LogoPositioner] Erro ao salvar:', err);
      alert(`Erro ao salvar composição: ${err.message}`);
    } finally {
      setIsSaving(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(0,0,0,0.88)',
        zIndex: 1200,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '1rem',
        backdropFilter: 'blur(8px)'
      }}
    >
      <div
        style={{
          background: isLight ? '#ffffff' : '#141418',
          border: isLight ? '1px solid #cbd5e1' : '1px solid var(--border-color)',
          borderRadius: '20px',
          padding: '1.5rem',
          width: '100%',
          maxWidth: '960px',
          maxHeight: '92vh',
          overflowY: 'auto',
          boxShadow: '0 25px 60px rgba(0,0,0,0.7)',
          display: 'flex',
          flexDirection: 'column',
          gap: '1.25rem'
        }}
      >
        {/* Header */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: isLight ? '1px solid #e2e8f0' : '1px solid rgba(255,255,255,0.08)', paddingBottom: '0.9rem' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem' }}>
            <div style={{ width: '38px', height: '38px', borderRadius: '10px', background: 'linear-gradient(135deg, #6366f1, #a855f7)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff' }}>
              <Move size={20} />
            </div>
            <div>
              <h3 style={{ margin: 0, color: isLight ? '#0f172a' : '#fff', fontSize: '1.2rem', fontWeight: 700 }}>
                {creativeTitle}
              </h3>
              <span style={{ fontSize: '0.8rem', color: isLight ? '#64748b' : 'var(--text-muted)' }}>
                Arraste a logo livremente pelo Canvas e ajuste a escala ideal para o seu anúncio.
              </span>
            </div>
          </div>
          <button
            onClick={onClose}
            style={{ background: 'transparent', border: 'none', color: isLight ? '#64748b' : '#94a3b8', cursor: 'pointer', padding: '0.4rem', borderRadius: '8px' }}
          >
            <X size={20} />
          </button>
        </div>

        {/* Corpo: Canvas Interativo + Painel Lateral de Ajustes */}
        <div style={{ display: 'grid', gridTemplateColumns: 'minmax(280px, 1fr) 300px', gap: '1.5rem', alignItems: 'start' }}>
          {/* Canvas Viewport */}
          <div
            ref={containerRef}
            style={{
              background: '#09090b',
              borderRadius: '14px',
              border: isLight ? '1px solid #cbd5e1' : '1px solid rgba(255,255,255,0.1)',
              padding: '1rem',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              minHeight: '440px',
              position: 'relative',
              boxShadow: 'inset 0 2px 10px rgba(0,0,0,0.5)'
            }}
          >
            {!imagesLoaded ? (
              <div style={{ color: '#94a3b8', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '0.6rem' }}>
                <RefreshCw size={24} className="spin text-indigo-400" />
                <span style={{ fontSize: '0.85rem' }}>Carregando arte e logo...</span>
              </div>
            ) : (
              <>
                <canvas
                  ref={canvasRef}
                  onPointerDown={handlePointerDown}
                  onPointerMove={handlePointerMove}
                  onPointerUp={handlePointerUp}
                  style={{
                    maxWidth: '100%',
                    maxHeight: '480px',
                    width: 'auto',
                    height: 'auto',
                    borderRadius: '8px',
                    cursor: isDragging ? 'grabbing' : 'grab',
                    touchAction: 'none',
                    boxShadow: '0 8px 24px rgba(0,0,0,0.6)'
                  }}
                  title="Clique e arraste para posicionar a logo"
                />
                <div style={{ marginTop: '0.75rem', fontSize: '0.75rem', color: '#a5b4fc', display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
                  <span>🖱️</span> <strong>Dica:</strong> Clique e arraste a logo sobre a imagem para movimentar.
                </div>
              </>
            )}
          </div>

          {/* Painel de Controles & Presets */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '1.25rem', background: isLight ? '#f8fafc' : 'rgba(255,255,255,0.02)', border: isLight ? '1px solid #e2e8f0' : '1px solid rgba(255,255,255,0.06)', borderRadius: '14px', padding: '1.25rem' }}>
            {/* 1. Atalhos de Cantos */}
            <div>
              <label style={{ display: 'block', fontSize: '0.78rem', fontWeight: 700, color: isLight ? '#334155' : '#e2e8f0', marginBottom: '0.5rem', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                📍 Atalhos Rápidos de Posição:
              </label>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.4rem' }}>
                <button
                  type="button"
                  onClick={() => applyPreset('top-left')}
                  className="secondary-button"
                  style={{ padding: '0.4rem 0.6rem', fontSize: '0.75rem', textAlign: 'center' }}
                >
                  ↖️ Superior Esq.
                </button>
                <button
                  type="button"
                  onClick={() => applyPreset('top-right')}
                  className="secondary-button"
                  style={{ padding: '0.4rem 0.6rem', fontSize: '0.75rem', textAlign: 'center' }}
                >
                  ↗️ Superior Dir.
                </button>
                <button
                  type="button"
                  onClick={() => applyPreset('bottom-left')}
                  className="secondary-button"
                  style={{ padding: '0.4rem 0.6rem', fontSize: '0.75rem', textAlign: 'center' }}
                >
                  ↙️ Inferior Esq.
                </button>
                <button
                  type="button"
                  onClick={() => applyPreset('bottom-right')}
                  className="secondary-button"
                  style={{ padding: '0.4rem 0.6rem', fontSize: '0.75rem', textAlign: 'center', borderColor: '#6366f1', color: '#6366f1' }}
                >
                  ↘️ Inferior Dir.
                </button>
              </div>
              <button
                type="button"
                onClick={() => applyPreset('center')}
                className="secondary-button"
                style={{ width: '100%', marginTop: '0.4rem', padding: '0.4rem 0.6rem', fontSize: '0.75rem', textAlign: 'center' }}
              >
                🎯 Centralizado
              </button>
            </div>

            {/* 2. Slider de Tamanho / Escala */}
            <div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.35rem' }}>
                <label style={{ fontSize: '0.78rem', fontWeight: 700, color: isLight ? '#334155' : '#e2e8f0', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                  📏 Tamanho da Logo:
                </label>
                <span style={{ fontSize: '0.75rem', fontWeight: 700, color: isLight ? '#4f46e5' : '#a5b4fc' }}>
                  {scalePercent}%
                </span>
              </div>
              <input
                type="range"
                min={6}
                max={50}
                value={scalePercent}
                onChange={(e) => setScalePercent(Number(e.target.value))}
                style={{ width: '100%', accentColor: '#6366f1', cursor: 'pointer' }}
              />
            </div>

            {/* 3. Opacidade e Rotação */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
              <div>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.35rem' }}>
                  <label style={{ fontSize: '0.78rem', fontWeight: 700, color: isLight ? '#334155' : '#e2e8f0' }}>
                    🌫️ Opacidade: {Math.round(opacity * 100)}%
                  </label>
                </div>
                <input
                  type="range"
                  min={0.3}
                  max={1.0}
                  step={0.05}
                  value={opacity}
                  onChange={(e) => setOpacity(Number(e.target.value))}
                  style={{ width: '100%', accentColor: '#6366f1', cursor: 'pointer' }}
                />
              </div>

              <div>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.35rem' }}>
                  <label style={{ fontSize: '0.78rem', fontWeight: 700, color: isLight ? '#334155' : '#e2e8f0' }}>
                    🔄 Rotação: {rotation}°
                  </label>
                </div>
                <input
                  type="range"
                  min={-45}
                  max={45}
                  value={rotation}
                  onChange={(e) => setRotation(Number(e.target.value))}
                  style={{ width: '100%', accentColor: '#6366f1', cursor: 'pointer' }}
                />
              </div>

              {/* Checkbox Sombra de Alto Contraste */}
              <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer', fontSize: '0.8rem', color: isLight ? '#334155' : '#e2e8f0', marginTop: '0.2rem' }}>
                <input
                  type="checkbox"
                  checked={hasShadow}
                  onChange={(e) => setHasShadow(e.target.checked)}
                  style={{ width: '16px', height: '16px', accentColor: '#6366f1' }}
                />
                <span>Sombra suave para contraste</span>
              </label>
            </div>

            {/* Posição Exata */}
            <div style={{ padding: '0.6rem', borderRadius: '8px', background: isLight ? '#f1f5f9' : 'rgba(0,0,0,0.3)', fontSize: '0.72rem', color: isLight ? '#64748b' : '#94a3b8', display: 'flex', justifyContent: 'space-between' }}>
              <span>X: <strong>{posX}%</strong></span>
              <span>Y: <strong>{posY}%</strong></span>
              <span>Escala: <strong>{scalePercent}%</strong></span>
            </div>
          </div>
        </div>

        {/* Footer com Botões de Ação */}
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.75rem', borderTop: isLight ? '1px solid #e2e8f0' : '1px solid rgba(255,255,255,0.08)', paddingTop: '1rem' }}>
          <button
            type="button"
            onClick={onClose}
            disabled={isSaving}
            className="secondary-button"
            style={{ padding: '0.65rem 1.25rem', fontSize: '0.85rem' }}
          >
            Cancelar
          </button>

          <button
            type="button"
            onClick={handleSave}
            disabled={isSaving || !imagesLoaded}
            className="primary-button"
            style={{
              padding: '0.65rem 1.5rem',
              fontSize: '0.85rem',
              fontWeight: 700,
              background: 'linear-gradient(135deg, #10b981, #059669)',
              border: 'none',
              color: '#fff',
              display: 'flex',
              alignItems: 'center',
              gap: '0.4rem',
              boxShadow: '0 4px 14px rgba(16, 185, 129, 0.35)'
            }}
          >
            {isSaving ? <RefreshCw size={15} className="spin" /> : <Check size={15} />}
            {isSaving ? 'Gravando Nova Imagem...' : 'Aplicar e Salvar Arte'}
          </button>
        </div>
      </div>
    </div>
  );
}
